import React, { ReactNode, ElementType, forwardRef } from 'react';
import { Box, BoxProps } from '@mui/material';
import { useElementScrollable } from '@/src/utils/useElementScrollable';

export interface HelperTextContentProps extends BoxProps<ElementType> {
  /**
   * Text on the left side
   */
  textLeft?: ReactNode;
  /**
   * Properties applied to text on the left side
   */
  textLeftProps?: BoxProps<ElementType>;
  /**
   * Text on the right side, expected to be characters count, e.g. '0 / 100'
   * It is recommended to also provide aria-label.
   */
  textRight?: ReactNode;
  /**
   * Properties applied to text on the right side
   */
  textRightProps?: BoxProps<ElementType>;
  /**
   * Input value
   */
  value?: string;
  /**
   * Input maxLength
   */
  maxLength?: number;
}

/**
 * Advanced content for the Field's HelperText
 *
 *  <TextField helperText={<HelperTextContent textLeft='Helper text' textRight='0/100' />}>
 */
export const HelperTextContent: React.FC<HelperTextContentProps> = forwardRef(
  ({ children, sx, className, textLeft, textLeftProps, textRight, textRightProps, value, maxLength, ...rest }, ref) => {
    const { sx: textLeftSx, className: textLeftClassName, ...restTextLeft } = textLeftProps || {};
    const { sx: textRightSx, className: textRightClassName, ...restTextRight } = textRightProps || {};
    const { isScrollable, elementRef } = useElementScrollable('x');

    return (
      <Box
        sx={{
          display: 'flex',
          gap: '12px',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          // inherit truncate text from helperText to pass it down
          overflow: 'inherit',
          whiteSpace: 'inherit',
          textOverflow: 'inherit',
          ...sx,
        }}
        component="span"
        className={['FlowHelperTextContent-root', className].filter(Boolean).join(' ')}
        ref={ref}
        {...rest}
      >
        {children}
        {(textLeft || textRight || !!maxLength) && (
          <Box
            sx={{
              // truncate text as helperText
              overflow: 'inherit',
              whiteSpace: 'inherit',
              textOverflow: 'inherit',
              ...textLeftSx,
            }}
            component="span"
            className={['FlowHelperTextContent-textLeft', textLeftClassName, isScrollable ? 'Flow-scrollable' : null]
              .filter(Boolean)
              .join(', ')}
            ref={elementRef}
            {...restTextLeft}
          >
            {textLeft}
          </Box>
        )}
        {(textRight || !!maxLength) && (
          <Box
            sx={{
              whiteSpace: 'nowrap',
              ...textRightSx,
            }}
            component="span"
            className={['FlowHelperTextContent-textRight', textRightClassName].filter(Boolean).join(' ')}
            {...(typeof textRight === 'string' &&
              textRight.includes('/') && {
                ['aria-label']: `Characters entered ${textRight}`,
              })}
            {...(maxLength && {
              ['aria-label']: `Characters entered ${value?.length || 0} / ${maxLength}`,
            })}
            {...restTextRight}
          >
            {textRight || `${value?.length || 0} / ${maxLength}`}
          </Box>
        )}
      </Box>
    );
  },
);
